import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useShellSlots } from './shellSlots'
import { downloadXlsx } from './xlsx'
import { downloadListPdf } from './listPdf'
import { fromStored, onTableStates, primeTableStates, saveTableState, storedState, toStored } from './tableStateStore'
import './DataTable.css'

/**
 * Tabelul listelor, cu markupul lui DataTables 1.10 (dom-ul din
 * DataTablesProperties, tema Bootstrap) ASA CUM RAMANE dupa ce trece
 * list_chrome.js peste el: cautarea si filtrele in antetul paginii, numarul de
 * randuri si paginarea in subsol, meniul de sortare in primul cap de coloana,
 * totalul ca insigna pe intrarea din bara, info ascuns. Aceleasi clase,
 * aceleasi id-uri (`<id>_wrapper`, `<id>_processing`, `<id>_filter`,
 * `<id>_length`, `<id>_paginate`), ca foile portalului sa deseneze la fel.
 *
 * In portal, DataTables desena controalele in tabel, list_chrome le muta, iar
 * pagina se rearanja de doua ori. Aici se deseneaza direct la locul final -
 * de-aia nu mai sare nimic.
 */

export interface TableColumn {
  index: number
  title: string
  titleHtml?: string
  orderable: boolean
  visible: boolean
  width?: number
  /**
   * Clasa coloanei din `columnDefs` (`a-left`, `center`), pusa si pe cap si pe
   * celula, ca in DataTables. Fara ea o coloana desenata `className: 'center'`
   * in portal ar iesi aliniata la stanga aici.
   */
  className?: string
  html(row: Record<string, unknown>): string
  /**
   * `createdCell` din DataTables: stilul pus pe CELULA, nu in ea. Coloana de
   * status a listei de tichete isi coloreaza fundalul asa, iar culoarea sta pe
   * `<td>` - nu se poate face din HTML-ul celulei.
   */
  cellStyle?(row: Record<string, unknown>): CSSProperties | undefined
  /** Clasa pusa pe RAND din aceeasi coloana (`$(cell).parent().addClass('vip')`). */
  rowClass?(row: Record<string, unknown>): string | undefined
}

export interface Order {
  column: number
  dir: 'asc' | 'desc'
}

export interface TableState {
  start: number
  length: number
  order: Order
  search: string
  /** Coloanele ascunse din meniul "Columns" (colvis), pe indice. */
  hidden?: number[]
  /**
   * Ordinea coloanelor, pe indice (`ColReorder` din DataTables): coloana
   * tinuta de cap si lasata pe alta isi schimba locul, si locul se tine
   * minte. `order` (sortarea) ramane pe INDICE, nu pe pozitie. Lipsa =
   * ordinea din model; o coloana care nu e in lista vine dupa cele din ea.
   */
  sequence?: number[]
}

export const LENGTHS = [10, 25, 50, 100]
export const DYNAMIC = 'dynamic'

/**
 * stateSave: aceeasi alegere la intoarcere - IN BAZA, pe om, ca in portal
 * (`General::tableStates` / `saveTableState`, `it_user_table_state`), cu
 * `localStorage` ca oglinda rapida. Vezi `tableStateStore.ts`.
 *
 * Lista se ridica pe loc cu ce e in browser; cand vin asezarile din baza
 * (o singura cerere pe pagina), a omului castiga - ea e cea care il urmeaza
 * de pe orice calculator, si cea in care a scris si portalul vechi.
 */
export function useTableState(id: string, initial: TableState): [TableState, (next: Partial<TableState>) => void] {
  const key = `od-table-state:${id}`
  const [state, set] = useState<TableState>(() => {
    /* Baza a raspuns deja (a doua lista de pe pagina, sau a doua pagina). */
    const stored = storedState(id)
    /* Cautarea nu se tine minte; porneste de la cea ceruta de ecran (`initial.search`,
       de obicei goala - Discovered o ia din `?search=`). */
    if (stored) return { ...initial, ...fromStored(stored, initial), start: 0, search: initial.search }
    try {
      const raw = window.localStorage.getItem(key)
      if (raw) {
        const saved = JSON.parse(raw) as Partial<TableState>
        return { ...initial, ...saved, start: 0, search: initial.search }
      }
    } catch {
      /* fara memorie: pornim de la implicit */
    }
    return initial
  })
  const touched = useRef(false)

  /* Se porneste, nu se asteapta: raspunsul corecteaza lista cand vine - dar
     numai daca omul n-a apucat sa schimbe ceva intre timp. */
  useEffect(() => {
    let alive = true
    const apply = () => {
      if (!alive || touched.current) return
      const stored = storedState(id)
      if (stored) set((prev) => ({ ...prev, ...fromStored(stored, prev), start: 0 }))
    }
    const off = onTableStates(apply)
    void primeTableStates().then(apply)
    return () => {
      alive = false
      off()
    }
  }, [id])

  /*
   * `auto` e o schimbare facuta de lista singura - potrivirea la inaltimea
   * ferestrei cand lungimea e Dynamic - nu o alegere a omului. Ea nu marcheaza
   * `touched` si nu se scrie in baza: altfel, la prima vizita dintr-un browser
   * nou, potrivirea de la montare (a) bloca asezarea omului venita din baza
   * si (b) o suprascria, o secunda mai tarziu, cu cea implicita a listei -
   * ordinea si coloanele ascunse alese de el se pierdeau.
   */
  const update = (next: Partial<TableState>, auto = false) => {
    if (!auto) touched.current = true
    set((prev) => {
      const merged = { ...prev, ...next }
      try {
        window.localStorage.setItem(
          key,
          JSON.stringify({ length: merged.length, order: merged.order, hidden: merged.hidden ?? [], sequence: merged.sequence ?? [] }),
        )
      } catch {
        /* ca mai sus */
      }
      /* Cautarea si pagina nu se scriu - o schimbare doar de-ale lor nu
         atinge baza. */
      if (!auto && (next.length !== undefined || next.order !== undefined || next.hidden !== undefined || next.sequence !== undefined)) {
        saveTableState(id, toStored(merged))
      }
      return merged
    })
  }
  return [state, update]
}

interface Props {
  id: string
  columns: TableColumn[]
  rows: Record<string, unknown>[]
  total: number
  /** Se cer date acum (spinnerul `dataTables_processing`). */
  fetching: boolean
  /** Lista nu s-a putut incarca (OdListStates: mesajul in tabel). */
  error?: string | null
  onRetry?: () => void
  state: TableState
  /** `auto`: schimbare facuta de lista singura (potrivirea Dynamic), nu de om. */
  onState(next: Partial<TableState>, auto?: boolean): void
  /**
   * Butoanele PROPRII ale modulului - ADD Device, Import CSV, Blank QR, Add
   * application - in BARA DE SUS, la dreapta filtrului.
   *
   * In portal modulele si le fac ca butoane DataTables, in acelasi grup cu
   * exporturile, deci DataTables le desena in banda de deasupra tabelului.
   * `list_chrome.js` goleste banda ca regula (`sweepStrip`): filtrele, Columns,
   * Refresh si exporturile pleaca fiecare la locul lui, iar ce ramane - chiar
   * butoanele astea - urca in `li.tk-header-tools` cu clasa `tk-header-extra`,
   * dupa care randul gol se strange (`tk-header-row-empty`, display:none).
   *
   * Aici se scriu direct in bara, ca sa nu mai fie desenate o data si mutate
   * dupa aceea. Pagina pune pe fiecare buton `tk-header-extra` - pe ea sta
   * masura din `list_chrome.css` (30px inaltime, langa cautare).
   */
  headerButtons?: ReactNode
  /** Unealta din antetul paginii, langa cautare (de pilda List/Tree). */
  headerView?: ReactNode
  /**
   * Ce scrie in casuta de cautare cat timp e goala, cand lista are de spus mai
   * mult decat „Search..." - la registre, DUPA CE se cauta („common name, e.g.
   * portal.example.ro"). In portal e `searchHint` din `Registries`, scris in
   * `placeholder`-ul campului de deasupra tabelului; campul ala a urcat in
   * antet, si textul urca odata cu el.
   */
  searchHint?: string
  /** Clasa in plus pe `#id_wrapper` (od-host-tree-on cand Tree View e deschis). */
  wrapperClass?: string
  /** Butonul si panoul de filtre; panoul primeste functia de inchidere (butonul Cancel). */
  filters?: { render(close: () => void): ReactNode; active: number }
  /** Ce sta inainte de tabel in pagina (widgeturi, modale). */
  children?: ReactNode
  rowId(row: Record<string, unknown>): string
  emptyHint?: string
  /**
   * Butoanele din subsol, dupa paginare - unde le muta list_chrome.js din
   * `DataTablesProperties.buttons`: Copy (exportul de pe pagina) si Refresh
   * (`dt.ajax.reload(null, false)`). `colvis` porneste butonul Columns, care
   * de pe 14 septembrie 2026 sta in BARA DE SUS, dupa Filters (meniul
   * coloanelor se deschide sub el, `tk-header-colvis`), nu in subsol.
   */
  footerButtons?: { copy?: boolean; colvis?: boolean; refresh?: () => void }
  /**
   * Butoane ale modulului in SUBSOL, dupa Refresh - Blank QR la Inventoree,
   * coborat de sus ca sa-i faca loc lui Columns. Acelasi markup ca in bara.
   */
  footerButtonsExtra?: ReactNode
  /** Numele fisierului la Excel (excelHtml5 ia titlul paginii); implicit titlul documentului. */
  exportName?: string
  /**
   * Randul desfasurat (ExpandRow.openDetails din general.js): clicul pe
   * `center.show_details` deschide sub rand un `tr.rowDetails > td.rowDetails`
   * pe toata latimea, cu ce intoarce functia; al doilea clic il inchide, iar
   * un alt rand deschis il inchide pe primul. Iconita trece plus -> minus,
   * randul primeste `details parent`. La redesenare (pagina, sortare,
   * Refresh) copilul dispare, ca in DataTables.
   */
  details?: (row: Record<string, unknown>) => ReactNode
  /**
   * Randul desfasurat tinut de pagina, nu de tabel (impreuna cu `onOpenRow`).
   *
   * Fara ele, tabelul isi tine singur randul deschis si il inchide la orice
   * raspuns nou - bine pentru listele unde redesenarea vine numai de la om.
   * Lista de tichete se reciteste insa si dupa o scriere facuta CHIAR din
   * randul deschis (Take over, avansare de stadiu, prioritate), si acolo
   * inchiderea il scotea pe om din tichet. Cand pagina tine randul, tabelul
   * nu-l mai inchide singur: hotaraste pagina cand.
   */
  openRow?: string | null
  onOpenRow?: (rowId: string | null) => void
  /**
   * `lengthMenu`, cand lista are alta lista de marimi decat cea obisnuita.
   *
   * Serverul are dreptul sa taie pagina mai jos decat cere pagina (lista de
   * tichete activa taie la 50, ca in `Tickets_summary::getData`). Cand meniul
   * ofera un numar mai mare decat plafonul, paginarea numara paginile dupa ce
   * a cerut, iar serverul intoarce mai putin: randurile dintre cele doua nu se
   * mai pot deschide de nicaieri.
   */
  lengths?: number[]
  /**
   * Uneltele de coloane din antet (Roxana, 14 septembrie 2026; intai pe
   * listele Inventoree): la mouse over pe un cap apar, mic, in coltul lui,
   * `x` (scoate coloana - acelasi lucru ca debifarea din meniul Columns) si
   * `+` (un meniu cu coloanele ascunse; cea aleasa intra IMEDIAT DUPA
   * coloana asta). Tot cu ele, capul se poate tine de mouse si lasa pe alt
   * cap - coloanele isi schimba locul (`state.sequence`). Coloanele fara
   * titlu (sageata de deschidere, casuta de bifat) si cele nesortabile
   * (Actions) nu se pot scoate si nici trage.
   */
  columnTools?: boolean
}

/**
 * INTRERUPATORUL butonului Columns (colvis) din bara de sus - ASCUNS
 * DEOCAMDATA, pe toate listele (Roxana, 18 septembrie 2026: "hai sa scoatem
 * din header butonul de columns, momentan ascunde-l in toate paginile").
 *
 * Poate pleca fiindca `+` si `-` de pe capul fiecarei coloane (`columnTools`)
 * fac deja acelasi lucru; ele raman neatinse, la fel si `state.hidden` - o
 * coloana scoasa cu `-` ramane scoasa, iar `+` o aduce inapoi.
 *
 * Paginile isi pastreaza `footerButtons={{ colvis: true }}`; butonul, meniul
 * si starea lor raman in cod. Ca sa se intoarca peste tot: `shown: true`.
 * E obiect, nu constanta, doar ca probele sa-l poata porni.
 */
export const COLUMNS_BUTTON = { shown: false }

/**
 * Coloanele in ordinea din `sequence` (ColReorder): intai cele numite acolo,
 * in ordinea lor, apoi restul in ordinea din model - o coloana noua in model
 * nu dispare doar fiindca omul si-a asezat lista inainte sa apara ea.
 */
export function orderColumns(columns: TableColumn[], sequence?: number[]): TableColumn[] {
  if (!sequence || sequence.length === 0) return columns
  const byIndex = new Map(columns.map((c) => [c.index, c]))
  const out: TableColumn[] = []
  for (const i of sequence) {
    const c = byIndex.get(i)
    if (c && !out.includes(c)) out.push(c)
  }
  for (const c of columns) if (!out.includes(c)) out.push(c)
  return out
}

/**
 * Ordinea noua dupa ce coloana `from` e lasata pe coloana `onto`: cea trasa ia
 * locul celeilalte, iar cele dintre ele se dau la o parte (ColReorder).
 */
export function movedSequence(sequence: number[], from: number, onto: number): number[] {
  const at = sequence.indexOf(onto)
  if (from === onto || at < 0 || !sequence.includes(from)) return sequence
  const rest = sequence.filter((i) => i !== from)
  rest.splice(at, 0, from)
  return rest
}

/** Coloana care nu se scoate si nu se trage: fara titlu, sau nesortabila (Actions). */
function lockedColumn(c: TableColumn): boolean {
  return !c.orderable || c.title.trim() === ''
}

/** `fa-plus-circle` -> `fa-minus-circle` pe randul deschis, cum facea jQuery pe DOM. */
export function openedCellHtml(html: string): string {
  return html.replace('fa-plus-circle', 'fa-minus-circle')
}

/** Textul unei celule, din HTML-ul ei - ce copiaza butonul Copy al lui DataTables. */
export function cellText(html: string): string {
  const box = document.createElement('div')
  box.innerHTML = html
  return (box.textContent ?? '').replace(/\s+/g, ' ').trim()
}

function grouped(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** Paginarea `simple_numbers` a lui DataTables: 7 butoane, cu puncte de suspensie. */
export function pageButtons(page: number, pages: number): (number | 'ellipsis')[] {
  const buttons = 7
  const half = Math.floor(buttons / 2)
  const range = (a: number, b: number) => Array.from({ length: b - a }, (_, i) => a + i)
  if (pages <= buttons) return range(0, pages)
  if (page <= half) return [...range(0, buttons - 2), 'ellipsis', pages - 1]
  if (page >= pages - 1 - half) return [0, 'ellipsis', ...range(pages - (buttons - 2), pages)]
  return [0, 'ellipsis', ...range(page - half + 2, page + half - 1), 'ellipsis', pages - 1]
}

/**
 * Casutele de bifat ale listelor, ca in portal.
 *
 * Portalul nu arata niciodata casuta browserului: `DataTablesMultiselect`
 * (`public/js/general.js`) si `EdMultiActions` (`public/js/easeedesk/easeedesk.js`)
 * trec `.allDevices/.oneDevice`, respectiv `.all-tickets/.select-ticket`, prin
 * iCheck cu skinul `icheckbox_flat-red` - un patrat desenat dintr-un sprite
 * (`public/images/vendor/icheck/skins/flat/red.png`, cu varianta @2x). Regulile
 * sunt in `public/css/app.css`, foaie pe care aplicatia o incarca deja
 * (PORTAL_STYLES din `vite.config.ts`), deci nu se adauga nici foaie, nici
 * pachet: se pune doar acelasi invelis pe care il pune iCheck.
 *
 * Invelisul lui iCheck e `<div class="icheckbox_flat-red">` cu `<input>`-ul
 * ascuns inauntru. Aici inputul ADEVARAT ramane tinta clicului (intins peste
 * toata casuta, transparent), nu un `<ins>` peste el ca la iCheck: paginile
 * asculta clicul pe `input.oneDevice` si citesc `target.checked`, deci
 * evenimentul trebuie sa vina de la input, nu de la un invelis.
 *
 * Sprite-ul are patru chipuri - nebifat, bifat, stins, bifat+stins. Nu are
 * unul PARTIAL, si nici portalul n-are: capul listei e ori bifat ori nu.
 * `indeterminate` se arata deci ca nebifat, la fel ca in portal.
 */
const ICHECK = 'icheckbox_flat-red'

/** Casutele de selectie: capul tabelului si celulele randurilor, nu si randul desfasurat. */
const ICHECK_CELLS = 'thead th input[type="checkbox"], tbody > tr > td:not(.rowDetails) input[type="checkbox"]'

function markIcheck(box: HTMLInputElement) {
  const wrap = box.parentElement
  if (!wrap || !wrap.classList.contains(ICHECK)) return
  wrap.classList.toggle('checked', box.checked && !box.indeterminate)
  wrap.classList.toggle('disabled', box.disabled)
}

function dressIcheck(root: HTMLElement | null) {
  if (!root) return
  root.querySelectorAll<HTMLInputElement>(ICHECK_CELLS).forEach((box) => {
    if (!box.parentElement?.classList.contains(ICHECK)) {
      const wrap = document.createElement('div')
      wrap.className = ICHECK
      wrap.style.position = 'relative'
      box.replaceWith(wrap)
      wrap.appendChild(box)
      /* Inputul acopera casuta si ramane tinta clicului, dar nu se vede. */
      box.style.position = 'absolute'
      box.style.top = '0'
      box.style.left = '0'
      box.style.width = '100%'
      box.style.height = '100%'
      box.style.margin = '0'
      box.style.padding = '0'
      box.style.border = '0'
      box.style.opacity = '0'
      box.style.cursor = box.disabled ? 'default' : 'pointer'
    }
    markIcheck(box)
  })
}

export function DataTable(props: Props) {
  const { id, columns, rows, total, state, onState } = props
  const slots = useShellSlots()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [sortOpen, setSortOpen] = useState(false)
  const [colvisOpen, setColvisOpen] = useState(false)
  /* Meniul coloanelor se deseneaza pe `body`, la coordonate fixe sub buton:
     antetul e `position: fixed` cu propriul context de stivuire, iar un meniu
     absolut inauntrul lui ramanea in spatele paginii sau sub tabel. La
     derulare sau redimensionare se inchide - ca in DataTables. */
  const colvisButton = useRef<HTMLButtonElement | null>(null)
  const [colvisAt, setColvisAt] = useState<{ top: number; right: number } | null>(null)
  useLayoutEffect(() => {
    if (!colvisOpen) {
      setColvisAt(null)
      return
    }
    const r = colvisButton.current?.getBoundingClientRect()
    setColvisAt(r ? { top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) } : { top: 60, right: 16 })
    const close = () => setColvisOpen(false)
    /* `scroll` se prinde in faza de captura, deci ajunge aici si derularea
       DIN lista de coloane (care e lunga si are `overflow-y: auto`). Aia nu
       inchide meniul - altfel nu se putea ajunge la coloanele de jos. */
    const closeOnPageScroll = (e: Event) => {
      const t = e.target
      if (t instanceof Element && t.closest('.tk-colvis-menu')) return
      close()
    }
    window.addEventListener('resize', close)
    window.addEventListener('scroll', closeOnPageScroll, true)
    return () => {
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', closeOnPageScroll, true)
    }
  }, [colvisOpen])
  const [copied, setCopied] = useState<number | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [ownOpenRow, setOwnOpenRow] = useState<string | null>(null)
  const controlled = props.onOpenRow !== undefined
  const openRow = controlled ? (props.openRow ?? null) : ownOpenRow
  const [searchDraft, setSearchDraft] = useState(state.search)
  const [dynamicFit, setDynamicFit] = useState(25)
  const [dynamicOn, setDynamicOn] = useState(() => {
    /* Prima vizita pe o lista o deschide pe Dynamic; alegerea omului ramane dupa. */
    const mark = `tk_dynamic_default_${id}`
    try {
      if (!window.localStorage.getItem(mark)) {
        window.localStorage.setItem(mark, '1')
        return true
      }
    } catch {
      /* fara memorie: alegerea de acum */
    }
    return false
  })

  useEffect(() => slots.claimHeaderTools(), [slots])

  /**
   * Invelisul iCheck se pune DUPA fiecare desenare: celulele vin din
   * `dangerouslySetInnerHTML`, iar cand HTML-ul unei celule se schimba React
   * rescrie tot continutul ei si invelisul dispare. Fara lista de dependinte
   * dinadins - se reface la orice desenare, oricat de mica.
   */
  useLayoutEffect(() => {
    dressIcheck(wrapperRef.current)
  })

  /* Bifa se vede in aceeasi clipa in care omul apasa, nu abia la desenarea urmatoare. */
  useEffect(() => {
    const root = wrapperRef.current
    if (!root) return
    const onChange = (event: Event) => {
      const box = event.target
      if (box instanceof HTMLInputElement && box.type === 'checkbox') markIcheck(box)
    }
    root.addEventListener('change', onChange)
    return () => root.removeEventListener('change', onChange)
  }, [])

  /*
   * Lista se lipeste de margini, ca in portal - fara portita.
   *
   * A existat una, `gutter`, timp de o livrare: registrul de certificate avea
   * deasupra tabelului bara lui de filtre si randul care spune ce e lista, si
   * fiecare pornea de la alta distanta de marginea ferestrei. Marginea de 20px
   * acoperea coloana zdrentuita; nu o indrepta. Ce a indreptat-o a fost sa nu
   * mai stea nimic deasupra tabelului: cautarea si filtrul au urcat in antet,
   * unde le tin toate celelalte liste. Vezi `CertificatesPage`.
   */
  useEffect(() => {
    document.body.classList.add('tk-list-flush')
    return () => document.body.classList.remove('tk-list-flush')
  }, [])

  useEffect(() => {
    slots.setTotal(total)
    return () => slots.setTotal(null)
  }, [slots, total])

  /* Cautarea pleaca la 400 ms dupa ultima tasta, ca in DataTables. */
  useEffect(() => {
    if (searchDraft === state.search) return
    const t = window.setTimeout(() => onState({ search: searchDraft, start: 0 }), 400)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft])

  /*
   * Dynamic: cate randuri incap intre capul tabelului si subsol (addDynamicLength).
   *
   * INALTIMEA RANDULUI SE MASOARA O DATA, nu la fiecare pagina de randuri.
   *
   * Tabelul are `table-layout: auto`: latimea coloanelor iese din TOATE
   * randurile de pe pagina. Un nume lung care intra in pagina lateste o
   * coloana, celelalte se strang, primul rand se rupe pe doua linii si e mai
   * inalt - deci incap mai putine randuri; cu mai putine, numele lung iese din
   * pagina, randul se strange la loc, si incap iar mai multe. Masurata din nou
   * la fiecare schimbare de `rows.length`, lungimea sarea intre doua valori
   * (22 -> 11 -> 22 ...); cand amandoua paginile erau deja in cache, sariturile
   * veneau fara nicio cerere intre ele, si React oprea pagina cu "Maximum update
   * depth exceeded" (#185) - asa a cazut Inventoree > Discovered, unde tabelul
   * sta pe 9/12 din latime si randurile se rup cel mai usor.
   *
   * Acum inaltimea se ia de pe primul rand de DATE (nu de pe randul "Nothing
   * here yet") si ramane pana la urmatoarea redimensionare a ferestrei. Ce
   * aduce lungimea aleasa nu mai poate schimba lungimea.
   */
  const rowHeightRef = useRef(0)
  useLayoutEffect(() => {
    const fit = (remeasure: boolean) => {
      const tbody = wrapperRef.current?.querySelector('tbody')
      const footer = document.getElementById('pageFooter')
      if (!tbody) return
      if (remeasure || rowHeightRef.current === 0) {
        const first = tbody.querySelector(':scope > tr[role="row"]')
        const measured = first ? first.getBoundingClientRect().height : 0
        if (measured >= 10) rowHeightRef.current = measured
      }
      const rowHeight = rowHeightRef.current || 40
      const top = tbody.getBoundingClientRect().top + window.scrollY
      const bottom = footer ? footer.getBoundingClientRect().top + window.scrollY : window.innerHeight
      setDynamicFit(Math.max(5, Math.floor((bottom - top - 2) / rowHeight)))
    }
    fit(false)
    let timer = 0
    const onResize = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => fit(true), 250)
    }
    window.addEventListener('resize', onResize)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('resize', onResize)
    }
  }, [rows.length])

  /* `state.length` e in lista: cand asezarea din baza vine cu alta lungime
     peste o lista Dynamic, potrivirea se pune la loc. */
  useEffect(() => {
    if (dynamicOn && state.length !== dynamicFit) onState({ length: dynamicFit, start: 0 }, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dynamicOn, dynamicFit, state.length])

  useEffect(() => {
    if (!sortOpen) return
    const away = () => setSortOpen(false)
    document.addEventListener('click', away)
    return () => document.removeEventListener('click', away)
  }, [sortOpen])

  /* Mesajul "Copied N rows to clipboard" al lui Buttons sta doua secunde. */
  useEffect(() => {
    if (copied === null) return
    const t = window.setTimeout(() => setCopied(null), 2000)
    return () => window.clearTimeout(t)
  }, [copied])

  /* Randurile noi (pagina, sortare, Refresh) vin fara copil, ca la DataTables. */
  useEffect(() => {
    if (!controlled) setOwnOpenRow(null)
  }, [rows, controlled])

  const onBodyClick = (event: MouseEvent<HTMLTableSectionElement>) => {
    if (!props.details) return
    const target = event.target as HTMLElement
    const trigger = target.closest('center.show_details, .dd-toggle')
    const tr = target.closest('tr')
    if (!trigger || !tr || !tr.id) return
    const next = openRow === tr.id ? null : tr.id
    if (controlled) props.onOpenRow?.(next)
    else setOwnOpenRow(next)
  }

  const hidden = useMemo(() => state.hidden ?? [], [state.hidden])
  const ordered = useMemo(() => orderColumns(columns, state.sequence), [columns, state.sequence])
  const visible = useMemo(() => ordered.filter((c) => c.visible && !hidden.includes(c.index)), [ordered, hidden])

  const toggleColumn = (index: number) => {
    onState({ hidden: hidden.includes(index) ? hidden.filter((i) => i !== index) : [...hidden, index] })
  }

  /* Uneltele din antet: `+` dupa o coloana, si tragerea unui cap pe altul. */
  const [addAfter, setAddAfter] = useState<number | null>(null)
  const [dragging, setDragging] = useState<number | null>(null)
  const [dropOn, setDropOn] = useState<number | null>(null)
  useEffect(() => {
    if (addAfter === null) return
    const away = () => setAddAfter(null)
    document.addEventListener('click', away)
    return () => document.removeEventListener('click', away)
  }, [addAfter])
  /* Ce se poate adauga: ascunse din meniu (nu `visible: false` din model). */
  const addable = useMemo(() => ordered.filter((c) => c.visible && hidden.includes(c.index)), [ordered, hidden])
  const fullSequence = () => ordered.map((c) => c.index)
  const addColumnAfter = (after: number, index: number) => {
    const seq = fullSequence().filter((i) => i !== index)
    seq.splice(seq.indexOf(after) + 1, 0, index)
    setAddAfter(null)
    onState({ hidden: hidden.filter((i) => i !== index), sequence: seq })
  }
  const dropColumn = (onto: number) => {
    const from = dragging
    setDragging(null)
    setDropOn(null)
    if (from === null || from === onto) return
    onState({ sequence: movedSequence(fullSequence(), from, onto) })
  }

  /* copyHtml5: capul si randurile vizibile, cu tab intre celule. */
  const copyPage = () => {
    const lines = [visible.map((c) => c.title).join('\t')]
    for (const row of rows) lines.push(visible.map((c) => cellText(c.html(row))).join('\t'))
    const text = lines.join('\n')
    const done = () => setCopied(rows.length)
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done, done)
    } else {
      const area = document.createElement('textarea')
      area.value = text
      document.body.appendChild(area)
      area.select()
      document.execCommand('copy')
      area.remove()
      done()
    }
  }
  /* Copy / Excel / PDF iau aceleasi celule: capul si randurile vizibile, ca text. */
  const pageCells = () => {
    const table = [visible.map((c) => c.title)]
    for (const row of rows) table.push(visible.map((c) => cellText(c.html(row))))
    return table
  }
  /* excelHtml5: un .xlsx scris local (`ui/xlsx.ts`). */
  const excelPage = () => downloadXlsx(pageCells(), props.exportName ?? document.title)
  /* pdfHtml5: serverul il aseaza cu html2pdf (`ui/listPdf.ts`). */
  const [pdfBusy, setPdfBusy] = useState(false)
  const pdfPage = () => {
    setPdfBusy(true)
    downloadListPdf(pageCells(), props.exportName ?? document.title)
      .catch((e: unknown) => window.alert(e instanceof Error ? e.message : 'The PDF could not be produced.'))
      .finally(() => setPdfBusy(false))
  }
  const sortable = useMemo(() => ordered.filter((c) => c.orderable && c.title.trim() !== ''), [ordered])
  const pages = Math.max(1, Math.ceil(total / state.length))
  const page = Math.min(Math.floor(state.start / state.length), pages - 1)

  const sortBy = (index: number) => {
    const dir = state.order.column === index && state.order.dir === 'asc' ? 'desc' : 'asc'
    onState({ order: { column: index, dir }, start: 0 })
  }

  const goTo = (p: number) => {
    if (p < 0 || p >= pages || p === page) return
    onState({ start: p * state.length })
  }

  const headerClass = (c: TableColumn) => {
    const parts = []
    if (!c.orderable) parts.push('sorting_disabled')
    else if (state.order.column === c.index) parts.push(state.order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc')
    else parts.push('sorting')
    if (state.order.column === c.index) parts.push('tk-sorted-by')
    if (c.className) parts.push(c.className)
    if (props.columnTools && !lockedColumn(c)) parts.push('tk-col-tools-th')
    if (dragging === c.index) parts.push('tk-col-dragging')
    if (dropOn === c.index && dragging !== null && dragging !== c.index) parts.push('tk-col-drop')
    return parts.join(' ')
  }

  /* `x` si `+` in coltul capului; clicul pe ele NU sorteaza. */
  const columnTools = (c: TableColumn) => {
    if (!props.columnTools || lockedColumn(c)) return null
    return (
      <>
      <span className="tk-col-tools" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="tk-col-tool tk-col-remove"
          title={`Hide column ${c.title}`}
          aria-label={`Hide column ${c.title}`}
          data-col-remove={c.index}
          onClick={() => {
            setAddAfter(null)
            toggleColumn(c.index)
          }}
        >
          &times;
        </button>
      </span>
      {/* + sta pe cusatura dintre coloana asta si urmatoarea, peste ele - arata
          unde intra coloana noua (Roxana, 14 septembrie 2026). Se vede
          intotdeauna, nu doar cand e ceva ascuns: fara el omul nu stie ca
          exista; cand nu e nimic de adaugat, lista o spune. */}
      {(
        <span className="tk-col-seam" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="tk-col-tool tk-col-add"
              title={`Add a column after ${c.title}`}
              aria-label={`Add a column after ${c.title}`}
              aria-haspopup="menu"
              aria-expanded={addAfter === c.index ? 'true' : 'false'}
              data-col-add={c.index}
              onClick={() => setAddAfter((v) => (v === c.index ? null : c.index))}
            >
              +
            </button>
          {addAfter === c.index && (
            <ul className="dropdown-menu tk-col-add-menu" role="menu" data-col-add-menu={c.index}>
              {/* Titlul nu e intrare de meniu (role="presentation"): tastatura
                  si probele numara doar `a[role="menuitem"]`. */}
              <li className="tk-col-add-menu-title" role="presentation">
                Add column
              </li>
              {addable.length === 0 && (
                <li className="tk-col-add-menu-empty" role="presentation">
                  All columns are shown
                </li>
              )}
              {addable.map((h) => (
                <li key={h.index} role="none">
                  <a
                    href="#"
                    role="menuitem"
                    data-col-add-idx={h.index}
                    onClick={(e) => {
                      e.preventDefault()
                      addColumnAfter(c.index, h.index)
                    }}
                  >
                    {h.title}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </span>
      )}
      </>
    )
  }

  const first = visible[0]

  const headerTools = slots.headerTools
    ? createPortal(
        <>
          <div id={`${id}_filter`} className="dataTables_filter input-icon tk-header-search">
            <label>
              <i className="fas fa-search"></i>
              <input
                type="search"
                className="form-control input-sm"
                placeholder={props.searchHint ?? 'Search...'}
                /* Casuta din antet are 200px, iar o indrumare de registru e mai
                   lunga de atat („common name, e.g. portal.example.ro" se taie
                   dupa „portal.e"). Pusa si in `title`, se citeste intreaga la
                   trecerea cu mouse-ul, in loc sa se piarda jumatate. */
                title={props.searchHint}
                aria-controls={id}
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
              />
            </label>
          </div>
          {props.filters && (
            <a
              className="btn btn-default btn-primary filters-toggle tk-header-filters"
              tabIndex={0}
              aria-controls={id}
              href="#"
              title="Filters"
              onClick={(e) => {
                e.preventDefault()
                setFiltersOpen((v) => !v)
              }}
            >
              <i className="fa fa-filter"></i>
              <span className="tk-filters-badge" style={{ display: props.filters.active > 0 ? undefined : 'none' }}>
                {props.filters.active > 0 ? props.filters.active : ''}
              </span>
            </a>
          )}
          {/* Columns, dupa Filters: butonul si meniul coloanelor stau intr-un
              invelis `position:relative`, ca lista sa se deschida sub buton
              (DataTable.css), nu deasupra ca in subsol (list_chrome.css).
              Ascuns deocamdata (18 septembrie 2026): vezi COLUMNS_BUTTON. Fara
              el nu ramane nici invelisul, deci nici golul lui de 8px. */}
          {props.footerButtons?.colvis && COLUMNS_BUTTON.shown && (
            <span className="tk-header-colvis">
              <button
                ref={colvisButton}
                className="btn btn-default buttons-collection buttons-colvis tk-list-colvis"
                tabIndex={0}
                aria-controls={id}
                type="button"
                aria-haspopup="true"
                aria-expanded={colvisOpen ? 'true' : 'false'}
                title="Columns"
                onClick={() => setColvisOpen((v) => !v)}
              >
                <i className="fas fa-columns" aria-hidden="true"></i>
              </button>
            </span>
          )}
          {/* Ordinea barii (14 septembrie 2026): cautarea si filtrul lipite,
              apoi Columns, apoi List/Tree cand pagina o are, apoi butoanele
              modulului. */}
          {props.headerView}
          {/* Butoanele modulului, ultimele: sweepStrip le pune in coada barii,
              dupa ce tryLift a urcat cautarea si filtrul. */}
          {props.headerButtons}
        </>,
        slots.headerTools,
      )
    : null

  const colvisMenu =
    colvisOpen && colvisAt
      ? createPortal(
          <>
            <div className="dt-button-background" onClick={() => setColvisOpen(false)}></div>
            <div className="dt-button-collection tk-colvis-menu" style={{ position: 'fixed', top: colvisAt.top, right: colvisAt.right, zIndex: 2010 }}>
              <div className="dropdown-menu">
                {ordered
                  .filter((c) => c.visible)
                  .map((c) => (
                    <a
                      key={c.index}
                      className={`dt-button buttons-columnVisibility dropdown-item${hidden.includes(c.index) ? '' : ' active'}`}
                      tabIndex={0}
                      aria-controls={id}
                      href="#"
                      data-cv-idx={c.index}
                      onClick={(e) => {
                        e.preventDefault()
                        toggleColumn(c.index)
                      }}
                    >
                      <span>{c.title}</span>
                    </a>
                  ))}
              </div>
            </div>
          </>,
          document.body,
        )
      : null

  const footerTools = slots.footerList
    ? createPortal(
        <div className="tk-list-bottom-actions">
          <div className="dataTables_length custom_length_class" id={`${id}_length`}>
            <label>
              <select
                name={`${id}_length`}
                aria-controls={id}
                className="form-control input-sm"
                value={dynamicOn ? DYNAMIC : state.length}
                onChange={(e) => {
                  if (e.target.value === DYNAMIC) {
                    setDynamicOn(true)
                    onState({ length: dynamicFit, start: 0 })
                  } else {
                    setDynamicOn(false)
                    onState({ length: Number(e.target.value), start: 0 })
                  }
                }}
              >
                {(props.lengths ?? LENGTHS).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
                <option className="tk-length-dynamic" value={DYNAMIC}>
                  Dynamic
                </option>
              </select>
            </label>
          </div>
          <div className="dataTables_paginate paging_simple_numbers" id={`${id}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page === 0 ? ' disabled' : ''}`} id={`${id}_previous`}>
                <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(page - 1))}>
                  <i className="fas fa-chevron-left" aria-hidden="true"></i>
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li className="paginate_button disabled" id={`${id}_ellipsis`} key={`e${i}`}>
                    <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li className={`paginate_button${b === page ? ' active' : ''}`} key={b}>
                    <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(b))}>
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${id}_next`}>
                <a href="#" aria-controls={id} tabIndex={0} onClick={(e) => (e.preventDefault(), goTo(page + 1))}>
                  <i className="fas fa-chevron-right" aria-hidden="true"></i>
                </a>
              </li>
            </ul>
          </div>
          {props.footerButtons?.copy && (
            <button className="btn btn-default buttons-copy buttons-html5 tk-footer-export" tabIndex={0} aria-controls={id} type="button" title="Copy" onClick={copyPage}>
              <span>
                <i className="fas fa-clipboard"></i>
              </span>
            </button>
          )}
          {props.footerButtons?.copy && (
            <button className="btn btn-default buttons-pdf buttons-html5 tk-footer-export" tabIndex={0} aria-controls={id} type="button" title="PDF" disabled={pdfBusy} onClick={pdfPage}>
              <span>
                <i className={pdfBusy ? 'fas fa-circle-notch fa-spin' : 'fas fa-file-pdf'}></i>
              </span>
            </button>
          )}
          {props.footerButtons?.copy && (
            <button className="btn btn-default buttons-excel buttons-html5 tk-footer-export" tabIndex={0} aria-controls={id} type="button" title="Excel" onClick={excelPage}>
              <span>
                <i className="fas fa-file-excel"></i>
              </span>
            </button>
          )}
          {props.footerButtons?.refresh && (
            <button className="btn btn-default tk-list-refresh" tabIndex={0} aria-controls={id} type="button" title="Refresh" onClick={props.footerButtons.refresh}>
              <span>
                <i className="fas fa-sync-alt"></i>
              </span>
            </button>
          )}
          {props.footerButtonsExtra}
        </div>,
        slots.footerList,
      )
    : null

  const from = total === 0 ? 0 : state.start + 1
  const to = Math.min(state.start + state.length, total)

  return (
    <>
      {headerTools}
      {colvisMenu}
      {footerTools}
      {copied !== null && (
        <div className="dt-button-info">
          <h2>Copy to clipboard</h2>
          <div>{copied === 1 ? 'Copied one row to clipboard' : `Copied ${copied} rows to clipboard`}</div>
        </div>
      )}
      {props.children}
      <div id={`${id}_wrapper`} className={`dataTables_wrapper form-inline dt-bootstrap no-footer${props.wrapperClass ? ` ${props.wrapperClass}` : ''}`} ref={wrapperRef}>
        {/*
          Banda de deasupra tabelului ramane in macheta, dar GOALA si stransa -
          exact ce lasa in urma `hideHeaderRowIfDone` din list_chrome.js. Ce
          desena DataTables aici a plecat: cautarea si filtrul in bara de sus,
          butoanele modulului si Columns tot acolo (`headerButtons`), Refresh /
          export in subsol. Fara `tk-header-row-empty` randul gol tot tinea
          cativa pixeli intre meniu si primul cap de coloana.
        */}
        <div className="row datatableHeader tk-header-row-empty">
          <div className="col-sm-6 dataTableF"></div>
          <div className="col-sm-6 dataTableA">
            <div className="dt-buttons btn-group"></div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12 quickFilters"></div>
        </div>
        <div className="row">
          {/* Foaia ascunde .userFilters; jQuery .toggle() scria display:block inline. */}
          <div className="col-sm-12 userFilters" style={{ display: filtersOpen ? 'block' : 'none' }}>
            {props.filters?.render(() => setFiltersOpen(false))}
          </div>
        </div>
        <div className="row">
          <div className="col-sm-12">
            <div
              className="dataTables_processing panel panel-default"
              id={`${id}_processing`}
              style={{ display: props.fetching ? 'block' : 'none' }}
            >
              <div className="spinnerballs default">
                <div className="bounce1"></div>
                <div className="bounce2"></div>
                <div className="bounce3"></div>
              </div>
            </div>
            <table
              className="table table-striped table-hover table-bordered dataTableM dataTable no-footer"
              width="100%"
              id={id}
              role="grid"
              aria-describedby={`${id}_info`}
            >
              <thead>
                <tr role="row">
                  {visible.map((c) => (
                    <th
                      key={c.index}
                      className={headerClass(c)}
                      tabIndex={c.orderable ? 0 : undefined}
                      aria-controls={id}
                      rowSpan={1}
                      colSpan={1}
                      style={c.width ? { width: c.width } : undefined}
                      aria-sort={state.order.column === c.index ? (state.order.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                      aria-label={c.orderable ? `${c.title}: activate to sort column ${state.order.column === c.index && state.order.dir === 'asc' ? 'descending' : 'ascending'}` : c.title}
                      onClick={c.orderable ? () => sortBy(c.index) : undefined}
                      /* Mouse-ul pleaca din cap fara sa aleaga: meniul lui + se
                         inchide si focusul pleaca de pe buton, altfel
                         `:focus-within` tinea uneltele la vedere. */
                      onMouseLeave={
                        props.columnTools && addAfter === c.index
                          ? (e) => {
                              setAddAfter(null)
                              const active = document.activeElement
                              if (active instanceof HTMLElement && e.currentTarget.contains(active)) active.blur()
                            }
                          : undefined
                      }
                      draggable={props.columnTools && !lockedColumn(c) ? true : undefined}
                      onDragStart={
                        props.columnTools && !lockedColumn(c)
                          ? (e) => {
                              setAddAfter(null)
                              setDragging(c.index)
                              try {
                                e.dataTransfer?.setData('text/plain', String(c.index))
                                if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
                              } catch {
                                /* jsdom n-are dataTransfer */
                              }
                            }
                          : undefined
                      }
                      onDragEnd={
                        props.columnTools
                          ? () => {
                              setDragging(null)
                              setDropOn(null)
                            }
                          : undefined
                      }
                      onDragOver={
                        props.columnTools && !lockedColumn(c)
                          ? (e) => {
                              if (dragging === null) return
                              e.preventDefault()
                              if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
                              if (dropOn !== c.index) setDropOn(c.index)
                            }
                          : undefined
                      }
                      onDragLeave={props.columnTools ? () => setDropOn((v) => (v === c.index ? null : v)) : undefined}
                      onDrop={
                        props.columnTools && !lockedColumn(c)
                          ? (e) => {
                              e.preventDefault()
                              dropColumn(c.index)
                            }
                          : undefined
                      }
                    >
                      {c.titleHtml ? <span dangerouslySetInnerHTML={{ __html: c.titleHtml }} /> : c.title}
                      {columnTools(c)}
                      {c === first && sortable.length > 0 && (
                        <span className={`dropdown tk-sort${sortOpen ? ' open' : ''}`} onClick={(e) => e.stopPropagation()}>
                          <a
                            href="#"
                            data-toggle="dropdown"
                            title="Sort by"
                            className="tk-sort-toggle"
                            onClick={(e) => {
                              e.preventDefault()
                              setSortOpen((v) => !v)
                            }}
                          >
                            <i className="fas fa-sort" aria-hidden="true"></i>
                          </a>
                          <ul className="dropdown-menu tk-sort-menu" data-sort-table={id}>
                            {sortable.map((s) => (
                              <li key={s.index}>
                                <a
                                  href="#"
                                  data-sort-column={s.index}
                                  className={state.order.column === s.index ? 'active' : undefined}
                                  onClick={(e) => {
                                    e.preventDefault()
                                    setSortOpen(false)
                                    sortBy(s.index)
                                  }}
                                >
                                  {s.title}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody onClick={onBodyClick}>
                {props.error ? (
                  <tr className="odd">
                    <td valign="top" colSpan={visible.length} className="dataTables_empty">
                      <div className="od-empty od-empty-error">
                        <p className="od-empty-head">This list did not load.</p>
                        <p className="od-empty-body">{props.error}</p>
                        {props.onRetry && (
                          <button type="button" className="btn btn-sm od-empty-retry" onClick={props.onRetry}>
                            <i className="fas fa-sync-alt" aria-hidden="true"></i> Try again
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr className="odd">
                    <td valign="top" colSpan={visible.length} className="dataTables_empty">
                      {state.search !== '' ? (
                        <div className="od-empty">
                          <p className="od-empty-head">Nothing matches what you are looking for.</p>
                          <p className="od-empty-body">Try fewer words, or clear the search to see the whole list.</p>
                          <button type="button" className="btn btn-sm od-empty-clear" onClick={() => setSearchDraft('')}>
                            Clear the search
                          </button>
                        </div>
                      ) : (
                        <div className="od-empty">
                          <p className="od-empty-head">Nothing here yet.</p>
                          {props.emptyHint && <p className="od-empty-body">{props.emptyHint}</p>}
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  rows.map((row, i) => {
                    const rid = props.rowId(row)
                    const opened = props.details !== undefined && openRow === rid
                    /* `createdCell` putea si sa puna o clasa pe rand (randul
                       VIP din lista de tichete); se aduna de la toate coloanele. */
                    const extra = visible
                      .map((c) => c.rowClass?.(row))
                      .filter((v): v is string => !!v)
                      .join(' ')
                    return (
                      <Fragment key={rid}>
                        <tr
                          id={rid}
                          role="row"
                          className={(i % 2 === 0 ? 'odd' : 'even') + (opened ? ' details parent' : '') + (extra ? ` ${extra}` : '')}
                        >
                          {visible.map((c) => (
                            <td
                              key={c.index}
                              className={c.className}
                              style={c.cellStyle?.(row)}
                              dangerouslySetInnerHTML={{ __html: opened ? openedCellHtml(c.html(row)) : c.html(row) }}
                            />
                          ))}
                        </tr>
                        {opened && (
                          <tr className="rowDetails">
                            <td className="rowDetails" colSpan={visible.length}>
                              {props.details?.(row)}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-4 aright">
            <div className="dataTables_info tk-info-hidden" id={`${id}_info`} role="status" aria-live="polite">
              Showing {grouped(from)} to {grouped(to)} of {grouped(total)} entries
            </div>
          </div>
        </div>
        <div className="clear"></div>
      </div>
    </>
  )
}
